import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";

import { validatePassword } from "../utils/validation";
import { Alert } from "../components/Alert";

interface Message {
    text: string;
    type: 'success' | 'error';
}

export const ResetPassword: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const apiUrl = import.meta.env.VITE_API_URL as string;
    const [password, setPassword] = useState<string>('');
    const [confirmPassword, setConfirmPassword] = useState<string>('');
    const [message, setMessage] = useState<Message>({ text: '', type: 'error' });
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isRedirecting, setIsRedirecting] = useState<boolean>(false); // New state for redirection
    const navigate = useNavigate();

    useEffect(() => {
        let redirectTimer: number;
        if (isRedirecting) {
            setMessage({ text: 'Password updated successfully! Redirecting to login...', type: 'success' });
            redirectTimer = window.setTimeout(() => {
                navigate('/login');
            }, 2000);
        }
        // Cleanup timer on component unmount or if isRedirecting changes
        return () => clearTimeout(redirectTimer);
    }, [isRedirecting, navigate]);


    useEffect(() => {
        const verifyToken = async () => {
            // Don't verify token if we are already in redirect mode
            if (isRedirecting) return;

            setIsLoading(true);
            try {
                const response = await fetch(`${apiUrl}/auth/reset-password/${token}`);
                if (!response.ok) {
                    const data = await response.json();
                    setMessage({ text: data.error || 'Invalid or expired token. You may need to request a new reset link.', type: 'error' });
                    // TODO: disable the form here or prevent submission?
                } else {
                    // Token is valid, clear any previous messages
                    setMessage({ text: '', type: 'error' });
                }
            } catch {
                setMessage({ text: 'Network error. Please check your connection.', type: 'error' });
            } finally {
                setIsLoading(false);
            }
        };

        verifyToken();
        // Added isRedirecting to dependencies
    }, [token, apiUrl, isRedirecting]);

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>): Promise<void> => {
        e.preventDefault();
        setIsLoading(true);
        setMessage({ text: '', type: 'error' });
        const passwordError = validatePassword(password, confirmPassword);
        if (passwordError) {
            setMessage({ text: passwordError, type: 'error' });
            setIsLoading(false);
            return;
        }
        const response = await fetch(`${apiUrl}/auth/reset-password/${token}`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({ password }),
        });
        const data = await response.json();
        if (response.ok) {
            setIsRedirecting(true);
        } else {
            setMessage({ text: data.error || 'Failed to update password. Please try again.', type: 'error' });
        }
        setIsLoading(false);
    };

    // If token was invalid from the start, show message and maybe not the form
    if (message.type === 'error' && /invalid|expired/i.test(message.text)) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="w-full max-w-md p-8 space-y-6 bg-amber-50 rounded-lg shadow-md text-center">
                    <Alert type={message.type} message={message.text} />
                    <Link to="/forgot-password" className="btn btn-primary mt-4">Request New Reset Link</Link>
                    <p className="text-sm mt-4">
                        Or <Link to="/login" className="text-primary hover:underline">Login</Link>
                    </p>
                </div>
            </div>
        );
    }


    return (
        <div className="flex items-center justify-center min-h-screen">
            <div className="w-full max-w-md p-8 space-y-6 bg-amber-50 rounded-lg shadow-md">
                {isRedirecting ? (
                    <div className="text-center">
                        {message.text && (
                            <Alert type={message.type} message={message.text} />
                        )}
                        <div className="flex justify-center items-center mt-6">
                            <span className="loading loading-spinner loading-lg text-primary"></span>
                        </div>
                        <p className="mt-2 text-sm">Please wait...</p>
                        <p className="mt-4">
                            If you are not redirected, <Link to="/login" className="text-primary hover:underline">click here to login</Link>.
                        </p>
                    </div>
                ) : (
                    <>
                        {message.text && !isRedirecting && (
                            <Alert type={message.type} message={message.text} />
                        )}
                        <h1 className="text-2xl font-bold text-center">Reset Your Password</h1>
                        <form onSubmit={handleSubmit} className="space-y-4" noValidate>
                            <div>
                                <label className="label">
                                    <span className="label-text">New Password</span>
                                </label>
                                <input
                                    type="password"
                                    placeholder="••••••••"
                                    className="w-full input input-bordered"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    disabled={isLoading}
                                />
                            </div>
                            <div>
                                <label className="label">
                                    <span className="label-text">Confirm Password</span>
                                </label>
                                <input
                                    type="password"
                                    placeholder="••••••••"
                                    className="w-full input input-bordered"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    disabled={isLoading}
                                />
                            </div>
                            <button type="submit" className="w-full btn btn-primary" disabled={isLoading}>
                                {isLoading ? <span className="loading loading-spinner"></span> : 'Reset Password'}
                            </button>
                        </form>
                        {!isLoading && (
                            <p className="text-sm text-center">
                                Remembered your password? <Link to="/login" className="text-primary hover:underline">Login</Link>
                            </p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};
